import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import fortressLogo from "../assets/fortress-logo.jpg";
import payQr from "../assets/payqr.png";
import { bankDetails, companyDetails } from "../data/invoiceDefaults";
import { formatDateSlash, formatPrintTimestamp } from "../utils/formatDate";
import { createQrCodeContent } from "../utils/qrCodeContent";
import BankDetailsTable from "./BankDetailsTable";
import InvoiceItemsTable from "./InvoiceItemsTable";
import TotalsBar from "./TotalsBar";

const inlineSettlementItemLimit = 2;
const itemPageLimit = 8;

const renderLines = (value) =>
  String(value || "")
    .split("\n")
    .filter(Boolean)
    .map((line) => <span key={line}>{line}</span>);

const chunkItems = (items) => {
  const chunks = [];

  for (let index = 0; index < items.length; index += itemPageLimit) {
    chunks.push({
      items: items.slice(index, index + itemPageLimit),
      startIndex: index,
    });
  }

  return chunks.length ? chunks : [{ items: [], startIndex: 0 }];
};

function DocumentHeader({ qrDataUrl }) {
  return (
    <>
      <header
        style={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          paddingRight: "16px",
        }}
      >
        {/* LEFT — Fortress logo */}
        <div className="invoice-logo-box" style={{ flexShrink: 0 }}>
          <img
            src={fortressLogo}
            alt="Fortress Safe"
            style={{
              display: "block",
              maxWidth: "100%",
              height: "auto",
            }}
          />
        </div>

        {/* CENTER — Company information */}
        <div
          className="company-heading"
          style={{
            flex: 1,
            minWidth: 0,
            padding: "0 16px",
          }}
        >
          <h1>{companyDetails.name}</h1>

          <address>
            <span>{companyDetails.address}</span>

            <span>
              Email:{" "}
              <a href={`mailto:${companyDetails.email}`}>
                {companyDetails.email}
              </a>
            </span>

            <span>
              Website:{" "}
              <a href={`https://${companyDetails.website}`}>
                {companyDetails.website}
              </a>
            </span>

            <span>Tel: {companyDetails.telephone}</span>
          </address>
        </div>

        {/* RIGHT — Existing invoice QR */}
        {qrDataUrl ? (
          <div
            style={{
              flexShrink: 0,
              width: "130px",
              height: "130px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={qrDataUrl}
              alt="Invoice QR code"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </div>
        ) : null}
      </header>

      <div className="invoice-gold-rule" />
    </>
  );
}

function InvoiceIntro({ invoice }) {
  return (
    <section className="invoice-intro">
      <div className="bill-to">
        <h2>B I L L I N G T O :</h2>

        {invoice.client.name ? (
          <p>{invoice.client.name}</p>
        ) : (
          <p>&nbsp;</p>
        )}

        {invoice.client.address ? (
          <p>{renderLines(invoice.client.address)}</p>
        ) : null}

        {invoice.client.phone ? (
          <p>Phone: {invoice.client.phone}</p>
        ) : null}

        {invoice.client.email ? (
          <p>Email: {invoice.client.email}</p>
        ) : null}

        {invoice.client.reference ? (
          <p>Reference: {invoice.client.reference}</p>
        ) : null}

        {invoice.client.volumeWeight ? (
          <p className="volume-line">
            VOLUME/WEIGHT: {invoice.client.volumeWeight}
          </p>
        ) : null}
      </div>

      <div className="invoice-meta">
        <p>
          <strong>
            ISSUE DATE: {formatDateSlash(invoice.info.issueDate)}
          </strong>
        </p>

        <p className="invoice-number-line">
          INVOICE NO.: {invoice.info.invoiceNumber}
        </p>

        <h2>B I L L I N G F R O M</h2>

        <p className="billing-from">
          {renderLines(companyDetails.billingFrom)}
        </p>
      </div>

      <div className="invoice-title">INVOICE:</div>
    </section>
  );
}

function SignatureField() {
  return (
    <div className="signature-field">
      <span>AUTHORIZED SIGNATURE:</span>
      <i aria-hidden="true" />
      <strong>FINANCE &amp; ACCOUNTS MANAGER</strong>
    </div>
  );
}

function SettlementSection({ invoice }) {
  const printTimestamp = formatPrintTimestamp(invoice.print.printedAt);

  return (
    <section className="invoice-settlement">
      <div className="bank-area">
        <h2>BANK ACCOUNTS DETAILS:</h2>

        <BankDetailsTable details={bankDetails} />

        <SignatureField />

        {printTimestamp ? (
          <p className="printed-date">
            PRINTED DATE &amp; TIME: {printTimestamp}
          </p>
        ) : null}
      </div>

      {/* PAYMENT QR — larger and pushed further upward */}
      <div
        className="payment-qr"
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          marginTop: "-50px",
        }}
      >
        <img
          src={payQr}
          alt="Payment QR code"
          style={{
            width: "160px",
            height: "160px",
            objectFit: "contain",
            display: "block",
          }}
        />
      </div>
    </section>
  );
}

function TermsSection({ invoice }) {
  return (
    <section className="invoice-terms">
      <h2>Terms:</h2>

      <p>
        <strong>Currency:</strong> {invoice.terms.currency}
      </p>

      <p>
        <strong>Payment:</strong> {invoice.terms.payment}
      </p>
    </section>
  );
}

function InvoicePage({
  invoice,
  qrDataUrl,
  items,
  startIndex,
  showIntro = false,
  showTotal = false,
  showSettlement = false,
  continuation = false,
}) {
  return (
    <article
      className="invoice-paper"
      aria-label="Invoice preview"
    >
      <DocumentHeader qrDataUrl={qrDataUrl} />

      {showIntro ? <InvoiceIntro invoice={invoice} /> : null}

      {continuation ? (
        <h2 className="continuation-heading">
          Invoice items continued
        </h2>
      ) : null}

      <InvoiceItemsTable
        items={items}
        currencySymbol={invoice.info.currencySymbol}
        startIndex={startIndex}
      />

      {showTotal ? (
        <TotalsBar
          items={invoice.items}
          currencySymbol={invoice.info.currencySymbol}
        />
      ) : null}

      {showSettlement ? (
        <>
          <TermsSection invoice={invoice} />

          <SettlementSection invoice={invoice} />

          <div
            className="invoice-footer-band"
            aria-hidden="true"
          />
        </>
      ) : null}
    </article>
  );
}

function SettlementPage({ invoice, qrDataUrl }) {
  return (
    <article
      className="invoice-paper invoice-paper-settlement"
      aria-label="Invoice settlement preview"
    >
      <DocumentHeader qrDataUrl={qrDataUrl} />

      <h2 className="continuation-heading">
        Invoice settlement
      </h2>

      <TotalsBar
        items={invoice.items}
        currencySymbol={invoice.info.currencySymbol}
      />

      <TermsSection invoice={invoice} />

      <SettlementSection invoice={invoice} />

      <div
        className="invoice-footer-band"
        aria-hidden="true"
      />
    </article>
  );
}

export default function InvoicePreview({ invoice }) {
  const [qrDataUrl, setQrDataUrl] = useState("");

  const qrContent = useMemo(
    () => createQrCodeContent(invoice),
    [invoice]
  );

  const canUseSinglePage =
    invoice.items.length <= inlineSettlementItemLimit;

  const itemChunks = chunkItems(invoice.items);

  useEffect(() => {
    let isActive = true;

    QRCode.toDataURL(qrContent, {
      errorCorrectionLevel: "M",
      margin: 1,
      scale: 5,
      color: {
        dark: "#000000",
        light: "#ffffff",
      },
    })
      .then((dataUrl) => {
        if (isActive) {
          setQrDataUrl(dataUrl);
        }
      })
      .catch(() => {
        if (isActive) {
          setQrDataUrl("");
        }
      });

    return () => {
      isActive = false;
    };
  }, [qrContent]);

  if (canUseSinglePage) {
    return (
      <div className="invoice-preview-pages">
        <InvoicePage
          invoice={invoice}
          qrDataUrl={qrDataUrl}
          items={invoice.items}
          startIndex={0}
          showIntro
          showTotal
          showSettlement
        />
      </div>
    );
  }

  return (
    <div className="invoice-preview-pages">
      {itemChunks.map((chunk, index) => (
        <InvoicePage
          key={`${chunk.startIndex}-${chunk.items.length}`}
          invoice={invoice}
          qrDataUrl={qrDataUrl}
          items={chunk.items}
          startIndex={chunk.startIndex}
          showIntro={index === 0}
          showTotal={index === itemChunks.length - 1}
          continuation={index > 0}
        />
      ))}

      <SettlementPage
        invoice={invoice}
        qrDataUrl={qrDataUrl}
      />
    </div>
  );
}